/**
 * What a booking, a payment, a place and a provider account can be — and the word
 * each one is written as.
 *
 * The flow board's frame F11 declares four short lists and one rule about them: *"A
 * status is never colour alone. Every one of these is written as a word wherever it
 * appears — on the User's booking, on the provider's diary, on the Admin's table.
 * Colour repeats the word; it never replaces it."*
 *
 * Three clients draw these. Without a word here, each of them invents its own — and
 * this product has already been bitten twice by one question in two vocabularies: the
 * sleep bands were *Badly, Alright and Well* on one screen and *Good, Normal and Bad*
 * on the next, and the journal's moods were a fourth scale for a question that already
 * had one. Twenty-three status values across three clients is the same fault with more
 * room to happen in.
 *
 * So the word travels with the value. A screen may still choose not to draw it; it may
 * not choose a different one.
 *
 * **Where the board and the specification disagree, the specification wins** — that is
 * the precedence this project already runs on. It happens once here, and it is noted
 * where it happens.
 *
 * **On the boundary.** This is the first thing in `contracts` a *module* imports, and
 * that is deliberate rather than an accident. The rule modules hold to is that they
 * know nothing about HTTP; a list of what a booking can be is not HTTP, it is the
 * domain's own vocabulary, and the alternative — the states in the module and the
 * words beside them here — is the same list written twice and one place to forget.
 * Nothing HTTP-shaped may be added to this file.
 */
// --- a booking ----------------------------------------------------------------
export const BOOKING_STATES = [
    'pending',
    'confirmed',
    'completed',
    'no_show',
    'cancelled_by_user',
    'cancelled_by_provider',
    'cancelled_by_admin',
    'expired',
];
export const BOOKING_WORDS = {
    // F11: "Held exists only for the seconds between confirming and the payment
    // clearing, and the User never sees the word."
    pending: 'Held',
    confirmed: 'Confirmed',
    completed: 'Attended',
    no_show: 'Did not attend',
    cancelled_by_user: 'Cancelled by the client',
    cancelled_by_provider: 'Cancelled by the professional',
    cancelled_by_admin: 'Cancelled by High Impact',
    /**
     * The eighth, which F11 does not draw.
     *
     * A hold that was never paid for. The board lists seven states and says a booking
     * "never skips one", which is true of the seven — this is the one that means no
     * booking was ever made, and the two lists below keep it off the screens that draw
     * bookings somebody actually has.
     */
    expired: 'Expired before payment',
};
/**
 * The short form, for a badge.
 *
 * The boards keep a colour map keyed by the word itself — `STATUS_CLS` — and its keys
 * are the short ones: *Live*, *Confirmed*, *Settled*, *Open*, *Invited*, *Paused*,
 * *Cancelled*, *Full*, *Refunded*, *Refused*. A table badge says **Cancelled**; F11
 * names the state **Cancelled by the User**, and 8.4 wants the reason on the row.
 *
 * Both are true and they are for different places, so both travel. The badge is what
 * fits in a chip; the word above is what a row or a record says. Sending only the
 * long one would have every table truncating it at the comma, which is three clients
 * inventing the same rule.
 */
export const BOOKING_BADGES = {
    pending: 'Held',
    confirmed: 'Confirmed',
    completed: 'Attended',
    no_show: 'Did not attend',
    cancelled_by_user: 'Cancelled',
    cancelled_by_provider: 'Cancelled',
    cancelled_by_admin: 'Cancelled',
    expired: 'Expired',
};
/**
 * The ones that mean something happened.
 *
 * 4.9a's Past rows "say what it came to". A hold somebody abandoned at the payment
 * sheet did not come to anything, and 8.4 is flatter still: *"Every booking arrives
 * **Confirmed**"* — a professional's diary that shows a row for somebody who never
 * paid is a diary that is wrong about its own first sentence.
 */
export const BOOKING_STATES_REAL = [
    'confirmed',
    'completed',
    'no_show',
    'cancelled_by_user',
    'cancelled_by_provider',
    'cancelled_by_admin',
];
/**
 * The ones that are a checkout rather than a booking.
 *
 * `pending` is somebody at the payment sheet right now; `expired` is somebody who
 * closed it. Neither belongs on a list of what a person has booked. The Admin's table
 * is the deliberate exception — 9.5 is "every appointment and introductory call", and
 * the desk follows the money, so it keeps a status filter that can name these.
 */
export const BOOKING_STATES_IN_FLIGHT = ['pending', 'expired'];
// --- a payment -----------------------------------------------------------------
export const PAYMENT_STATES = [
    'started',
    'held',
    'processing',
    'succeeded',
    'refused',
    'abandoned',
    'refunded',
    'partially_refunded',
];
export const PAYMENT_WORDS = {
    started: 'Started',
    held: 'Held',
    /** In flight at the bank — a 3-D Secure step finished in a banking app. */
    processing: 'Processing',
    /**
     * F11 calls this state **Taken**; 9.3a calls the row **Settled** twice — *"a row
     * that went on saying Settled underneath it"* — and the table's own filter chip is
     * Settled. The specification outranks the flow document, so Settled it is.
     */
    succeeded: 'Settled',
    refused: 'Refused',
    abandoned: 'Abandoned',
    refunded: 'Refunded',
    /**
     * 9.3a: "After it, the row reads Refunded." That is written about a full refund,
     * and a part refund said the same word would hide the difference on the one screen
     * whose job is the money — so it gets its own, and the amount is beside it.
     */
    partially_refunded: 'Partly refunded',
};
// --- a place in a class ----------------------------------------------------------
export const ENROLMENT_STATES = ['enrolled', 'cancelled_by_user', 'cancelled_refunded'];
/**
 * F11 draws four: **Enrolled, Attended, Did not attend, Given back**.
 *
 * The table stores three, because attendance is not a status — it is a fact recorded
 * afterwards, and a place is still *enrolled* right up until the class runs. Merging
 * them into one column would mean a place with no way to say "enrolled, and the class
 * has not happened yet", which is the state most places are in most of the time.
 *
 * So the four the board asks for are computed from the two we hold, by `placeWord`
 * below — which is the only place that merge happens.
 */
export const ENROLMENT_WORDS = {
    enrolled: 'Enrolled',
    cancelled_by_user: 'Given back',
    cancelled_refunded: 'Given back, refunded',
};
/**
 * The word F11 actually asks for, which needs the status and the attendance together.
 *
 * `attended` is null until the class has run. Before then a place reads *Enrolled*;
 * afterwards it reads what happened.
 */
export function placeWord(status, attended) {
    if (status !== 'enrolled')
        return ENROLMENT_WORDS[status];
    if (attended === null)
        return 'Enrolled';
    return attended ? 'Attended' : 'Did not attend';
}
// --- a professional's paid bookings -------------------------------------------------
/**
 * Decision 5.38 (client, 2026-09-27): paid calls are switched on per professional, by
 * the desk, once. `off` — free call only; `requested` — she asked from her Services
 * screen; `on` — she takes paid bookings.
 */
export const PAID_BOOKINGS_STATES = ['off', 'requested', 'on'];
export const PAID_BOOKINGS_WORDS = {
    off: 'Free call only',
    requested: 'Asked for paid bookings',
    on: 'Taking paid bookings',
};
/**
 * Decision 5.42 (client, 2026-09-28): how a paid appointment is charged — the words for
 * `payment_mode` (primitives.ts `PAYMENT_MODES`). `full` is the default (5.40).
 */
export const PAYMENT_MODE_WORDS = {
    full: 'Full price when booked',
    deposit: 'A deposit when booked, the rest on the day',
};
// --- a provider account -----------------------------------------------------------
export const PROVIDER_STATES = ['invited', 'live', 'paused', 'removed'];
export const PROVIDER_WORDS = {
    // F11: "Invited means the link is sent and the details are not finished: not
    // listed… There is no state between Invited and Live."
    invited: 'Invited',
    live: 'Live',
    paused: 'Paused',
    /**
     * The fourth, which F11 does not draw.
     *
     * 9.2 offers *Pause, restore or remove a listing*, so it is a state the desk can
     * reach; the board lists the three a listing passes through on its way to being
     * live and leaves out the one that ends it.
     */
    removed: 'Removed',
};
// --- a person's account -----------------------------------------------------------
//
// Drawn on the desk wherever a user is listed or opened. Added 2026-09-21: the
// status was served from the first day and had no agreed word for it, so each
// screen would have chosen its own — which is the failure `StatusWord` exists to
// prevent, and the reason the four sets above are written here rather than in a
// component.
export const ACCOUNT_STATES = ['pending_verification', 'active', 'suspended', 'closed'];
export const ACCOUNT_WORDS = {
    /** Signed up, code not yet entered. Not "Unverified", which sounds like a refusal. */
    pending_verification: 'Awaiting their code',
    active: 'Active',
    /** Ours, and reversible. Distinct from Closed, which is theirs and is not. */
    suspended: 'Suspended',
    /** They asked. The row survives the person, which is why this is a state and not a deletion. */
    closed: 'Closed',
};
// --- a city -----------------------------------------------------------------------
export const CITY_STATES = ['active', 'coming_soon', 'inactive'];
export const CITY_WORDS = {
    active: 'Open',
    /**
     * The phone app already says "Coming soon" to a person who picks one of these,
     * and the desk saying "Pending" about the same city is how two halves of one
     * product end up describing the same row differently.
     */
    coming_soon: 'Coming soon',
    inactive: 'Closed',
};
// --- a question the host puts to the room -----------------------------------------
export const QUESTION_STATES = ['open', 'answered', 'closed'];
export const QUESTION_WORDS = {
    /** Live in the room, taking answers. */
    open: 'Open',
    /** Answered by this person — the state on a participant's row, not the question's. */
    answered: 'Answered',
    /**
     * Taking no more. The class console's "Open again" puts it back, which is why
     * this is closed rather than finished.
     */
    closed: 'Closed',
};
// --- the four pillars, in words ---------------------------------------------------
//
// Added 2026-09-21 with Mentors. The words were nowhere: the directory served the
// enum and every screen made up its own label, which is how the boards ended up
// with **"Vetted mentors providers"** — a heading assembled out of a tab name.
//
// `tools/providers_data.py` is where the designer writes them and this is the same
// set, because a label the API does not serve is a label three clients each invent.
/** The tab, and the filter chip. One word where one word will do. */
export const PILLAR_LABELS = {
    mind_body: 'Health',
    personal_money: 'Money',
    business: 'Business',
    /** "Mentors", not "Mentorship": it sits beside three one-word fields, and it is
     *  the people you are choosing between rather than the subject. */
    mentorship: 'Mentors',
};
/** Home's four tiles, which have room for the longer name. */
export const PILLAR_TILES = {
    mind_body: 'Health & Mind',
    personal_money: 'Money & Tax',
    business: 'Business & Legal',
    mentorship: 'Mentors',
};
/**
 * The heading over a pillar's list, **written out rather than assembled**.
 *
 * Three read "Vetted <label> providers" and the fourth would have read "Vetted
 * mentors providers", which is what happens when a sentence is built out of a noun
 * chosen to be a tab. That exact string was drawn on a board.
 */
export const PILLAR_HEADINGS = {
    mind_body: 'Vetted health providers',
    personal_money: 'Vetted money providers',
    business: 'Vetted business providers',
    mentorship: 'Mentors who have been there',
};
/**
 * The singular, for the sentence when nothing matched.
 *
 * *"No health provider in Vancouver lists ADHD Support."* For the fourth that is
 * "No mentor in Vancouver lists…", not "No mentors provider".
 */
export const PILLAR_ONE = {
    mind_body: 'health provider',
    personal_money: 'money provider',
    business: 'business provider',
    mentorship: 'mentor',
};
// --- a class, and an event --------------------------------------------------------
//
// Added 2026-09-21, and found the same way the pillar words were: by listing every
// enum the desk *displays* and checking each against what could render it. 9.6 and
// 9.7 both draw a status column and `StatusWord` knew seven vocabularies, none of
// them these two. Both screens would have invented their own words.
export const CLASS_STATES = ['draft', 'published', 'live', 'finished', 'cancelled'];
export const CLASS_WORDS = {
    draft: 'Draft',
    /** On the Learn tab and open for enrolment. "Published", not "Live" — see below. */
    published: 'Published',
    /**
     * Running, right now, with people in the room.
     *
     * The distinction matters on 9.6: a class that is *published* can be edited and
     * cancelled, and one that is *live* cannot be either — there are people in it.
     */
    live: 'Live now',
    finished: 'Finished',
    cancelled: 'Cancelled',
};
export const EVENT_STATES = ['draft', 'published', 'archived', 'cancelled'];
export const EVENT_WORDS = {
    draft: 'Draft',
    published: 'Listed',
    /**
     * Taken off the list, and not the same as cancelled.
     *
     * An archived event happened, or its day passed; a cancelled one was called off.
     * 9.7 shows both and the difference is the whole reason there are four states.
     */
    archived: 'Archived',
    cancelled: 'Cancelled',
};
// --- how somebody feels ------------------------------------------------------------
//
// A fixed set of five, on the client's decision of 2026-09-25: "mood is a fixed set;
// the admin does not make moods, and their faces are the ones the prototype draws."
// It replaced a 1–5 score and a desk-maintained tag list that no screen drew.
//
// Five expressions, not five points on a curve — the prototype's words: "Joyful and
// Calm are both pleasant and Anxious and Tired are not the same kind of unpleasant."
// This is the order the faces sit in, not a ranking. The words a member reads are
// the phone's (health.moods.* in packages/i18n); these are the values. Mirrored by the
// `mood` Postgres enum — test/boundaries.test.ts holds the two together.
export const MOODS = ['joyful', 'calm', 'neutral', 'anxious', 'tired'];
/**
 * The two that count towards 5.10b's run — "3 low days in a row". The prototype's
 * LOW_MOODS, and a count rather than a diagnosis, as the screen says.
 */
export const LOW_MOODS = ['anxious', 'tired'];
/**
 * Worst first, used for one question only: which day of a stretch was the hardest
 * (5.10a). The prototype's MOOD_RANK, which it notes "is not shown anywhere, because
 * ranking somebody's feelings on a screen they log them on would be a product with an
 * opinion about how they should feel." Never sort or colour a list of moods by this.
 */
export const MOOD_HARDEST_FIRST = ['anxious', 'tired', 'neutral', 'calm', 'joyful'];
// --- the runs the app watches for ---------------------------------------------------
//
// 5.10b's "3 low days in a row" and 5.5c's run of heavy-carbohydrate days: the two
// patterns worked out on the phone, each of which offers one kind of professional.
// The desk maps each to a profession (the nudge_category_slugs setting), so the pair is
// written once here. It used to be offered to the desk as the admin-made mood words,
// which could never match these keys; mood is a fixed five since 2026-09-25.
export const NUDGE_CONCERNS = ['low_mood', 'high_carbohydrate'];
export const NUDGE_CONCERN_WORDS = {
    /** LOW_MOODS logged three days running. */
    low_mood: 'A run of low moods',
    high_carbohydrate: 'A run of heavy-carbohydrate days',
};
/** A report's state on the desk (App Store guideline 1.2, 2026-10-02). */
export const REPORT_STATUS_WORDS = {
    open: 'Waiting',
    removed: 'Removed',
    kept: 'Kept',
};
/** The app's download-link requests on the desk (client, 2026-10-02). */
export const APP_PLATFORM_WORDS = { ios: 'iOS', android: 'Android' };
export const APP_SOURCE_WORDS = {
    website: 'Website',
    onboarding: 'Professional sign-up',
};
//# sourceMappingURL=states.js.map